import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Icon from '../../components/AppIcon';
import MainSidebar from '../../components/ui/MainSidebar';
import WorkOrderCard from './components/WorkOrderCard';
import ClientHistoryPanel from './components/ClientHistoryPanel';
import CommunicationPanel from './components/CommunicationPanel';
import Select from '../../components/ui/Select';
import LiveIndicator from '../../components/ui/LiveIndicator';
import NotificationCenter from '../../components/ui/NotificationCenter';
import { useRealtimeWorkOrders } from '../../hooks/useRealtimeWorkOrders';
import { useRealtimeQuotations } from '../../hooks/useRealtimeQuotations';
import { workOrderService } from '../../services/workOrderService';
import { saleNoteService } from '../../services/saleNoteService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import NavAcciones from '../../components/NavAcciones';


const isDev = import.meta.env?.DEV;
const devLog = (...args) => { if (isDev) console.log('[GearControl]', ...args); };

const CONTEXT_TIMEOUT_MS = 10000;

// ─── Status badge helper ──────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pendiente: 'bg-yellow-100 text-yellow-800',
    pagado: 'bg-green-100 text-green-800',
    vencido: 'bg-red-100 text-red-800',
  };
  const cls = map?.[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
    </span>
  );
};

const SalesManagement = () => {
  const navigate = useNavigate();
  const { sessionReady, contextReady, contextError, refreshContext, signOut, userProfile } = useAuth();

  // --- DIAGNOSTIC ---
  useEffect(() => {
    const runDiagnostic = async () => {
      console.log('VENTAS COMPONENT MOUNTED');
      const { data, error } = await supabase?.from('work_orders')?.select('*')?.limit(5);
      if (error) console.error('VENTAS FETCH ERROR:', error);
      else console.log('VENTAS FETCH SUCCESS:', data);
    };
    runDiagnostic();
  }, []);

  // ── Work orders list state ──
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('fecha-desc');
  const [showClientForm, setShowClientForm] = useState(false);

  // ── Cotizaciones autorizadas / Generar OT state ──
  const [approvedQuotes, setApprovedQuotes] = useState([]);
  const [selectedApprovedQuote, setSelectedApprovedQuote] = useState(null);
  const [generatingOT, setGeneratingOT] = useState(false);
  const [otGenerated, setOtGenerated] = useState(null);
  const [loadingApprovedQuotes, setLoadingApprovedQuotes] = useState(false);
  const [otError, setOtError] = useState(null);

  // ── Sales chart state ──
  const [salesChartData, setSalesChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  // ── Sales goal state ──
  const [goalAmount, setGoalAmount] = useState(0);
  const [goalId, setGoalId] = useState(null);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState('');
  const [savingGoal, setSavingGoal] = useState(false);
  const [goalToast, setGoalToast] = useState(null);

  // ── CxC state ──
  const [cxcList, setCxcList] = useState([]);
  const [loadingCxc, setLoadingCxc] = useState(false);
  const [showCxcForm, setShowCxcForm] = useState(false);
  const [cxcForm, setCxcForm] = useState({ amount: '', due_date: '', status: 'pendiente', notes: '' });
  const [savingCxc, setSavingCxc] = useState(false);

  // ── CxP state ──
  const [cxpList, setCxpList] = useState([]);
  const [loadingCxp, setLoadingCxp] = useState(false);
  const [showCxpForm, setShowCxpForm] = useState(false);
  const [cxpForm, setCxpForm] = useState({ reference: '', amount: '', due_date: '', status: 'pendiente', notes: '' });
  const [savingCxp, setSavingCxp] = useState(false);

  // ── Financial Planning state ──
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [newExpense, setNewExpense] = useState({ concept: '', amount: '', notes: '' });
  const [editingExpense, setEditingExpense] = useState(null);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [contributionMargin, setContributionMargin] = useState(40);
  const [marginInput, setMarginInput] = useState('40');
  const [editingMargin, setEditingMargin] = useState(false);
  const [financialSettingsId, setFinancialSettingsId] = useState(null);
  const [savingFinancial, setSavingFinancial] = useState(false);
  const [expenseError, setExpenseError] = useState(null);

  // ── Timeout state ──
  const [timedOut, setTimedOut] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    devLog('SalesManagement mounted — sessionReady:', sessionReady, 'contextReady:', contextReady);
    if (contextReady) {
      if (timeoutRef?.current) clearTimeout(timeoutRef?.current);
      setTimedOut(false);
      return;
    }
    timeoutRef.current = setTimeout(() => {
      if (!contextReady) {
        console.warn('[GearControl] SALES TIMEOUT — context not ready after 10s');
        setTimedOut(true);
      }
    }, CONTEXT_TIMEOUT_MS);
    return () => { if (timeoutRef?.current) clearTimeout(timeoutRef?.current); };
  }, [contextReady, sessionReady]);

  useEffect(() => {
    if (contextReady && timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
      setTimedOut(false);
    }
  }, [contextReady]);

  const handleRetry = async () => {
    setRetrying(true);
    setTimedOut(false);
    try { await refreshContext(); } finally { setRetrying(false); }
  };

  const handleSignOut = async () => {
    try { await signOut(); navigate('/signin'); }
    catch (err) { console.error('Sign out error:', err); }
  };

  // ── Load all data on mount ──
  useEffect(() => {
    fetchApprovedQuotes();
    fetchSalesChart();
    fetchCxc();
    fetchCxp();
    fetchFixedExpenses();
  }, [userProfile?.company_id]);

  // ── Fetch approved quotations ──
  const fetchApprovedQuotes = async () => {
    setLoadingApprovedQuotes(true);
    setOtError(null);
    try {
      const { data, error } = await supabase
        ?.from('quotations')
        ?.select(`
          id,
          folio_cotizacion,
          status,
          total,
          created_at,
          reception_id,
          authorized_items_snapshot,
          items_snapshot,
          receptions:reception_id(
            id,
            client_id,
            vehicles:vehicle_id(id, vin, marca, modelo, year, placas, motor, color),
            clients:client_id(id, nombre, telefono, email)
          )
        `)
        ?.eq('status', 'aprobada')
        ?.order('created_at', { ascending: false });

      if (error) {
        console.error('[SalesManagement] fetchApprovedQuotes error:', error);
        setOtError('Error al cargar cotizaciones aprobadas: ' + (error?.message || JSON.stringify(error)));
        setApprovedQuotes([]);
        return;
      }

      const quoteIds = (data || [])?.map(q => q?.id);
      let usedQuoteIds = new Set();
      if (quoteIds?.length > 0) {
        const { data: woData } = await supabase
          ?.from('work_orders')
          ?.select('quote_id')
          ?.in('quote_id', quoteIds);
        usedQuoteIds = new Set((woData || [])?.map(w => w?.quote_id));
      }

      const available = (data || [])?.filter(q => !usedQuoteIds?.has(q?.id));
      setApprovedQuotes(available);
    } catch (err) {
      console.error('[SalesManagement] fetchApprovedQuotes catch:', err);
      setOtError('Error al cargar cotizaciones aprobadas: ' + (err?.message || 'Error desconocido'));
      setApprovedQuotes([]);
    } finally {
      setLoadingApprovedQuotes(false);
    }
  };

  // ── Fetch sales chart data ──
  const fetchSalesChart = async () => {
    setLoadingChart(true);
    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)?.toISOString();
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)?.toISOString();
      const monthLabel = now?.toLocaleString('es-MX', { month: 'long', year: 'numeric' });

      // Real sales = sum of approved quotations this month
      const { data: quotesData } = await supabase
        ?.from('quotations')
        ?.select('total')
        ?.eq('status', 'aprobada')
        ?.gte('created_at', monthStart)
        ?.lte('created_at', monthEnd);

      const realSales = (quotesData || [])?.reduce((acc, q) => acc + (Number(q?.total) || 0), 0);

      // Goal from sales_goals
      const monthDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;
      const { data: goalData } = await supabase
        ?.from('sales_goals')
        ?.select('id, goal_amount')
        ?.eq('month', monthDate)
        ?.maybeSingle();

      const fetchedGoalAmount = Number(goalData?.goal_amount) || 0;
      setGoalAmount(fetchedGoalAmount);
      setGoalId(goalData?.id || null);
      setGoalInput(String(goalData?.goal_amount || ''));

      setSalesChartData([{
        mes: monthLabel?.charAt(0)?.toUpperCase() + monthLabel?.slice(1),
        'Ventas reales': Math.round(realSales),
        'Objetivo': Math.round(fetchedGoalAmount),
      }]);
    } catch (err) {
      console.error('[SalesManagement] fetchSalesChart error:', err);
      setSalesChartData([]);
    } finally {
      setLoadingChart(false);
    }
  };

  // ── Save sales goal ──
  const handleSaveGoal = async () => {
    setSavingGoal(true);
    try {
      const now = new Date();
      const monthDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;
      const numericGoal = Number(goalInput);
      if (isNaN(numericGoal) || numericGoal < 0) {
        setGoalToast({ type: 'error', msg: 'Error al guardar objetivo' });
        return;
      }
      if (goalId) {
        const { error } = await supabase
          ?.from('sales_goals')
          ?.update({ goal_amount: numericGoal })
          ?.eq('id', goalId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          ?.from('sales_goals')
          ?.insert({ month: monthDate, goal_amount: numericGoal });
        if (error) throw error;
      }
      setGoalAmount(numericGoal);
      setEditingGoal(false);
      setGoalToast({ type: 'success', msg: 'Objetivo guardado correctamente' });
      setTimeout(() => setGoalToast(null), 3000);
      await fetchSalesChart();
    } catch (err) {
      console.error('[SalesManagement] handleSaveGoal error:', err);
      setGoalToast({ type: 'error', msg: 'Error al guardar objetivo' });
      setTimeout(() => setGoalToast(null), 3000);
    } finally {
      setSavingGoal(false);
    }
  };

  // ── Generate OT ──
  const handleGenerateOT = async () => {
    if (!selectedApprovedQuote) return;
    setGeneratingOT(true);
    setOtError(null);
    try {
      const { data: existing } = await supabase
        ?.from('work_orders')
        ?.select('id, folio_ot')
        ?.eq('quote_id', selectedApprovedQuote?.id)
        ?.maybeSingle();

      if (existing?.id) {
        setOtError(`Esta cotización ya tiene una Orden de Trabajo generada (${existing?.folio_ot || existing?.id})`);
        setGeneratingOT(false);
        return;
      }

      const authorizedItems = selectedApprovedQuote?.authorized_items_snapshot;
      if (!authorizedItems || authorizedItems?.length === 0) {
        setOtError('Esta cotización no tiene ítems autorizados guardados. Revisa autorización antes de generar OT.');
        setGeneratingOT(false);
        return;
      }

      const { data: newOT, error: insertError } = await supabase
        ?.from('work_orders')
        ?.insert({
          quote_id: selectedApprovedQuote?.id,
          reception_id: selectedApprovedQuote?.reception_id,
          work_status: 'created',
          scope_of_work: authorizedItems,
          assigned_to_user_id: null,
        })
        ?.select('id, folio_ot')
        ?.single();

      if (insertError) {
        console.error('[SalesManagement] handleGenerateOT insert error:', insertError);
        setOtError('Error al generar OT: ' + (insertError?.message || 'Error desconocido'));
        return;
      }

      console.log('[SalesManagement] OT creada:', newOT);
      setOtGenerated(newOT);
      setSelectedApprovedQuote(null);

      // ── Notify taller role users ──
      try {
        const { data: tallerUsers } = await supabase
          ?.from('user_profiles')
          ?.select('id')
          ?.eq('role', 'taller');
        if (tallerUsers?.length > 0) {
          const notifRows = tallerUsers?.map(u => ({
            user_id: u?.id,
            type: 'work_order_status_change',
            title: 'Nueva OT lista para Taller',
            message: `Se generó la OT ${newOT?.folio_ot || newOT?.id} y está lista para ser atendida en Taller.`,
            work_order_id: newOT?.id,
          }));
          await supabase?.from('notifications')?.insert(notifRows);
        }
      } catch (notifErr) {
        console.warn('[SalesManagement] No se pudo enviar notificación a Taller:', notifErr);
      }

      await fetchApprovedQuotes();
      await fetchSalesChart();
    } catch (err) {
      console.error('[SalesManagement] handleGenerateOT catch:', err);
      setOtError('Error al generar OT: ' + (err?.message || 'Error desconocido'));
    } finally {
      setGeneratingOT(false);
    }
  };

  // ── CxC CRUD ──
  const fetchCxc = async () => {
    setLoadingCxc(true);
    try {
      const companyId = userProfile?.company_id;
      let query = supabase
        ?.from('accounts_receivable')
        ?.select(`
          id, amount, due_date, status, notes, created_at,
          quote_id,
          clients:client_id(id, nombre)
        `)
        ?.order('due_date', { ascending: true });

      if (companyId) {
        query = query?.eq('company_id', companyId);
      }

      const { data, error } = await query;
      if (!error) setCxcList(data || []);
      else console.error('[SalesManagement] fetchCxc error:', error);
    } catch (err) {
      console.error('[SalesManagement] fetchCxc error:', err);
    } finally {
      setLoadingCxc(false);
    }
  };

  const handleSaveCxc = async () => {
    if (!cxcForm?.amount) return;
    setSavingCxc(true);
    try {
      const companyId = userProfile?.company_id;
      const payload = {
        amount: Number(cxcForm?.amount),
        due_date: cxcForm?.due_date || null,
        status: cxcForm?.status || 'pendiente',
        notes: cxcForm?.notes || null,
      };
      if (companyId) payload.company_id = companyId;

      const { error } = await supabase?.from('accounts_receivable')?.insert(payload);
      if (!error) {
        setCxcForm({ amount: '', due_date: '', status: 'pendiente', notes: '' });
        setShowCxcForm(false);
        await fetchCxc();
      } else {
        console.error('[SalesManagement] handleSaveCxc error:', error);
      }
    } catch (err) {
      console.error('[SalesManagement] handleSaveCxc error:', err);
    } finally {
      setSavingCxc(false);
    }
  };

  const handleUpdateCxcStatus = async (id, newStatus) => {
    try {
      await supabase?.from('accounts_receivable')?.update({ status: newStatus })?.eq('id', id);
      await fetchCxc();
    } catch (err) {
      console.error('[SalesManagement] handleUpdateCxcStatus error:', err);
    }
  };

  // ── CxP CRUD ──
  const fetchCxp = async () => {
    setLoadingCxp(true);
    try {
      const { data, error } = await supabase
        ?.from('accounts_payable')
        ?.select(`
          id, amount, due_date, status, notes, reference, created_at,
          providers:supplier_id(id, name)
        `)
        ?.order('due_date', { ascending: true });
      if (!error) setCxpList(data || []);
    } catch (err) {
      console.error('[SalesManagement] fetchCxp error:', err);
    } finally {
      setLoadingCxp(false);
    }
  };

  const handleSaveCxp = async () => {
    if (!cxpForm?.amount) return;
    setSavingCxp(true);
    try {
      const { error } = await supabase?.from('accounts_payable')?.insert({
        reference: cxpForm?.reference || null,
        amount: Number(cxpForm?.amount),
        due_date: cxpForm?.due_date || null,
        status: cxpForm?.status || 'pendiente',
        notes: cxpForm?.notes || null,
      });
      if (!error) {
        setCxpForm({ reference: '', amount: '', due_date: '', status: 'pendiente', notes: '' });
        setShowCxpForm(false);
        await fetchCxp();
      }
    } catch (err) {
      console.error('[SalesManagement] handleSaveCxp error:', err);
    } finally {
      setSavingCxp(false);
    }
  };

  const handleUpdateCxpStatus = async (id, newStatus) => {
    try {
      await supabase?.from('accounts_payable')?.update({ status: newStatus })?.eq('id', id);
      await fetchCxp();
    } catch (err) {
      console.error('[SalesManagement] handleUpdateCxpStatus error:', err);
    }
  };

  // ── Financial Planning CRUD ──
  const fetchFixedExpenses = async () => {
    try {
      const now = new Date();
      const monthDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;
      const { data: expData } = await supabase
        ?.from('fixed_expenses')
        ?.select('*')
        ?.eq('month', monthDate)
        ?.order('created_at', { ascending: true });
      setFixedExpenses(expData || []);

      const { data: settingsData } = await supabase
        ?.from('financial_settings')
        ?.select('*')
        ?.eq('month', monthDate)
        ?.maybeSingle();
      const margin = Number(settingsData?.contribution_margin_percent) || 40;
      setContributionMargin(margin);
      setMarginInput(String(settingsData?.contribution_margin_percent || 40));
      setFinancialSettingsId(settingsData?.id || null);
    } catch (err) {
      console.error('[SalesManagement] fetchFixedExpenses error:', err);
    }
  };

  const handleSaveExpense = async () => {
    if (!newExpense?.concept || !newExpense?.amount) return;
    setExpenseError(null);
    setSavingFinancial(true);
    try {
      const now = new Date();
      const monthDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;
      const { error } = await supabase?.from('fixed_expenses')?.insert({
        month: monthDate,
        concept: String(newExpense?.concept)?.trim(),
        amount: parseFloat(newExpense?.amount),
        notes: newExpense?.notes ? String(newExpense?.notes)?.trim() : null,
      });
      if (error) {
        console.error('[SalesManagement] handleSaveExpense insert error:', error);
        setExpenseError('Error al guardar: ' + (error?.message || JSON.stringify(error)));
        return;
      }
      setNewExpense({ concept: '', amount: '', notes: '' });
      setShowAddExpense(false);
      await fetchFixedExpenses();
      await fetchSalesChart();
    } catch (err) {
      console.error('[SalesManagement] handleSaveExpense catch:', err);
      setExpenseError('Error al guardar gasto: ' + (err?.message || 'Error desconocido'));
    } finally {
      setSavingFinancial(false);
    }
  };

  const handleUpdateExpense = async () => {
    if (!editingExpense?.concept || !editingExpense?.amount) return;
    setExpenseError(null);
    setSavingFinancial(true);
    try {
      const { error } = await supabase?.from('fixed_expenses')?.update({
        concept: String(editingExpense?.concept)?.trim(),
        amount: parseFloat(editingExpense?.amount),
        notes: editingExpense?.notes ? String(editingExpense?.notes)?.trim() : null,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', editingExpense?.id);
      if (error) {
        console.error('[SalesManagement] handleUpdateExpense update error:', error);
        setExpenseError('Error al actualizar: ' + (error?.message || JSON.stringify(error)));
        return;
      }
      setEditingExpense(null);
      await fetchFixedExpenses();
      await fetchSalesChart();
    } catch (err) {
      console.error('[SalesManagement] handleUpdateExpense catch:', err);
      setExpenseError('Error al actualizar gasto: ' + (err?.message || 'Error desconocido'));
    } finally {
      setSavingFinancial(false);
    }
  };

  const handleDeleteExpense = async (id) => {
    try {
      await supabase?.from('fixed_expenses')?.delete()?.eq('id', id);
      await fetchFixedExpenses();
    } catch (err) {
      console.error('[SalesManagement] handleDeleteExpense error:', err);
    }
  };

  const handleSaveMargin = async () => {
    setSavingFinancial(true);
    try {
      const now = new Date();
      const monthDate = `${now?.getFullYear()}-${String(now?.getMonth() + 1)?.padStart(2, '0')}-01`;
      const marginVal = Number(marginInput);
      if (isNaN(marginVal) || marginVal <= 0) return;
      if (financialSettingsId) {
        await supabase?.from('financial_settings')?.update({
          contribution_margin_percent: marginVal,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', financialSettingsId);
      } else {
        const { data } = await supabase?.from('financial_settings')?.insert({
          month: monthDate,
          contribution_margin_percent: marginVal,
        })?.select('id')?.single();
        setFinancialSettingsId(data?.id || null);
      }
      setContributionMargin(marginVal);
      setEditingMargin(false);
    } catch (err) {
      console.error('[SalesManagement] handleSaveMargin error:', err);
    } finally {
      setSavingFinancial(false);
    }
  };

  // ── Work orders hooks ──
  const {
    workOrders,
    loading: ordersLoading,
    error: ordersError,
    isLive: ordersIsLive,
    lastUpdate: ordersLastUpdate
  } = useRealtimeWorkOrders({ sessionReady, contextReady });

  const {
    quotations,
    isLive: quotationsIsLive,
    lastUpdate: quotationsLastUpdate
  } = useRealtimeQuotations();

  const mockClientHistory = [
    {
      folioOT: 'OT-000045',
      vehiculo: 'Toyota Corolla 2020',
      fecha: '2025-11-20T10:00:00',
      status: 'completado',
      statusLabel: 'Completado',
      montoTotal: 8500.00,
      servicios: ['Cambio de aceite', 'Afinación mayor', 'Balanceo']
    },
    {
      folioOT: 'OT-000032',
      vehiculo: 'Toyota Corolla 2020',
      fecha: '2025-08-15T14:30:00',
      status: 'completado',
      statusLabel: 'Completado',
      montoTotal: 3200.00,
      servicios: ['Servicio de frenos', 'Rotación de llantas']
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'Todos los estados' },
    { value: 'recepcionado', label: 'Recepcionado' },
    { value: 'inspeccionado', label: 'Inspeccionado' },
    { value: 'cotizando', label: 'Cotizando' },
    { value: 'aprobado', label: 'Aprobado' }
  ];

  const sortOptions = [
    { value: 'fecha-desc', label: 'Más recientes' },
    { value: 'fecha-asc', label: 'Más antiguos' },
    { value: 'folio-asc', label: 'Folio (A-Z)' },
    { value: 'folio-desc', label: 'Folio (Z-A)' }
  ];

  const handleExpandOrder = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const handleStartQuotation = async (workOrder) => {
    try {
      await saleNoteService?.ensureDraftForWorkOrder(
        workOrder?.id,
        workOrder?.companyId || workOrder?._raw?.company_id || null
      );
    } catch (err) {
      console.error('Error ensuring sale note/quotation drafts:', err);
    }
    navigate('/quotation-management', { state: { workOrder } });
  };

  const handleSendToInspection = async (workOrder) => {
    try {
      await workOrderService?.updateStatus(workOrder?.id, 'inspeccionado');
      navigate('/technical-inspection', { state: { workOrder } });
    } catch (error) {
      console.error('Error sending to inspection:', error);
    }
  };

  const handleContactClient = (workOrder) => {
    setSelectedWorkOrder(workOrder);
    setShowClientForm(true);
  };

  const handleSaveClient = (clientData) => {
    console.log('Cliente guardado:', clientData);
    setShowClientForm(false);
  };

  const handleSendEmail = (emailData) => { console.log('Email enviado:', emailData); };
  const handleMakeCall = (phoneNumber) => { window.location.href = `tel:${phoneNumber}`; };
  const handleSendWhatsApp = (whatsappData) => {
    const message = encodeURIComponent(whatsappData?.message);
    window.open(`https://wa.me/52${whatsappData?.to}?text=${message}`, '_blank');
  };
  const handleSelectClient = (workOrder) => { setSelectedWorkOrder(workOrder); };

  const normalizedWorkOrders = (workOrders ?? [])?.map((wo) => ({
    id: wo?.id,
    folioOt: wo?.folio_ot ?? wo?.numero_ot ?? wo?.folio ?? '',
    folioOT: wo?.folio_ot ?? wo?.numero_ot ?? wo?.folio ?? '',
    estadoOt: wo?.estado_ot ?? wo?.estadoOT ?? '',
    estadoOT: wo?.estado_ot ?? wo?.estadoOT ?? '',
    fechaIngreso: wo?.fecha_ingreso ?? wo?.fechaIngreso ?? null,
    kilometraje: wo?.kilometraje ?? null,
    clienteId: wo?.cliente_id ?? null,
    vehicleId: wo?.vehicle_id ?? null,
    companyId: wo?.company_id ?? null,
    unidad: wo?.vehicle ? {
      tipo: wo?.vehicle?.tipo,
      marca: wo?.vehicle?.marca,
      modelo: wo?.vehicle?.modelo,
      año: wo?.vehicle?.año,
      placas: wo?.vehicle?.placas,
      vin: wo?.vehicle?.vin,
      color: wo?.vehicle?.color,
      motor: wo?.vehicle?.motor,
      kilometraje: wo?.kilometraje
    } : null,
    cliente: wo?.client ? {
      nombre: wo?.client?.nombre,
      empresa: wo?.client?.empresa,
      telefono: wo?.client?.telefono,
      email: wo?.client?.email
    } : null,
    itemsCotizacion: wo?.items_cotizacion ?? wo?.itemsCotizacion ?? null,
    _raw: wo,
  }));

  const filteredOrders = normalizedWorkOrders?.filter((order) => {
    if (filterStatus === 'all') return true;
    return order?.estadoOt === filterStatus;
  });

  const sortedOrders = [...filteredOrders]?.sort((a, b) => {
    switch (sortBy) {
      case 'fecha-desc': return new Date(b?.fechaIngreso) - new Date(a?.fechaIngreso);
      case 'fecha-asc': return new Date(a?.fechaIngreso) - new Date(b?.fechaIngreso);
      case 'folio-asc': return a?.folioOt?.localeCompare(b?.folioOt);
      case 'folio-desc': return b?.folioOt?.localeCompare(a?.folioOt);
      default: return 0;
    }
  });

  // ── Totals ──
  const totalCxcPendiente = cxcList
    ?.filter(r => r?.status === 'pendiente' || r?.status === 'vencido')
    ?.reduce((acc, r) => acc + (Number(r?.amount) || 0), 0);

  const totalCxpPendiente = cxpList
    ?.filter(r => r?.status === 'pendiente' || r?.status === 'vencido')
    ?.reduce((acc, r) => acc + (Number(r?.amount) || 0), 0);

  const fmtMXN = (n) => Number(n)?.toLocaleString('es-MX', { minimumFractionDigits: 2 });

  // ── Financial Planning computed values ──
  const totalFixedExpenses = fixedExpenses?.reduce((sum, e) => sum + Number(e?.amount || 0), 0);
  const breakEvenPoint = contributionMargin > 0 ? totalFixedExpenses / (contributionMargin / 100) : 0;
  const realSalesAmt = salesChartData?.[0]?.['Ventas reales'] || 0;
  const goalAmt = goalAmount;
  const diffVsGoal = realSalesAmt - goalAmt;
  const diffVsBreakEven = realSalesAmt - breakEvenPoint;

  const EXPENSE_CONCEPTS = [
    'Renta', 'Nómina', 'Luz', 'Agua', 'Internet',
    'Software', 'Publicidad', 'Teléfono', 'Mantenimiento', 'Otros'
  ];

  // ─── Timeout error panel ──────────────────────────────────────────────────
  if (timedOut) {
    return (
      <div className="min-h-screen bg-background">
        <MainSidebar />
        <main className="lg:ml-64">
          <div className="p-6 lg:p-8">
            <div className="max-w-md mx-auto mt-20 bg-card rounded-xl shadow-elevation-2 p-8 text-center">
              <Icon name="WifiOff" className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-heading font-bold text-foreground mb-2">No se pudo cargar Ventas</h2>
              <p className="text-muted-foreground mb-6">
                {contextError || 'No se pudo establecer la sesión o el contexto de empresa.'}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={handleRetry} disabled={retrying}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors">
                  {retrying ? <Icon name="Loader2" className="w-4 h-4 animate-spin" /> : <Icon name="RefreshCw" className="w-4 h-4" />}
                  Reintentar
                </button>
                <button onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 px-5 py-2.5 bg-destructive text-destructive-foreground rounded-lg font-medium hover:bg-destructive/90 transition-colors">
                  <Icon name="LogOut" className="w-4 h-4" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!contextReady && !timedOut) {
    return (
      <div className="min-h-screen bg-background">
        <MainSidebar />
        <main className="lg:ml-64">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Icon name="Loader2" className="w-10 h-10 text-primary animate-spin mx-auto mb-3" />
              <p className="text-muted-foreground font-medium">Cargando sesión…</p>
              {contextError && <p className="text-sm text-red-500 mt-2 max-w-xs">{contextError}</p>}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background">
      <MainSidebar />
      <main className="lg:ml-64">
        <div className="p-6 lg:p-8">
          <div className="max-w-7xl mx-auto">
            <NavAcciones
              onRegresar={() => navigate(-1)}
              onSalir={async () => { await supabase?.auth?.signOut(); navigate('/signin'); }}
              label="Gestión de Ventas"
            />

            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2 pl-10 lg:pl-0">
                  <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground leading-tight">Ventas</h1>
                  <LiveIndicator
                    isLive={ordersIsLive || quotationsIsLive}
                    lastUpdate={ordersLastUpdate || quotationsLastUpdate}
                  />
                  <NotificationCenter />
                </div>
                <p className="text-muted-foreground pl-10 lg:pl-0">Administra clientes, cotizaciones y seguimiento de órdenes</p>
              </div>
            </div>

            {ordersError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <Icon name="AlertCircle" className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-red-800">{ordersError}</p>
              </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                PLANEACIÓN FINANCIERA
            ══════════════════════════════════════════════════════════════ */}
            <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center gap-2 mb-6">
                <Icon name="TrendingUp" className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-heading font-bold text-foreground">Planeación Financiera</h2>
              </div>

              {/* ── A) Gastos fijos del mes ── */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold text-foreground">Gastos fijos del mes</h3>
                  {!showAddExpense && (
                    <button
                      onClick={() => setShowAddExpense(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Icon name="Plus" className="w-3.5 h-3.5" />
                      Agregar gasto
                    </button>
                  )}
                </div>

                {/* Expense list */}
                {fixedExpenses?.length > 0 ? (
                  <div className="overflow-x-auto mb-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-2 px-2 text-muted-foreground font-medium">Concepto</th>
                          <th className="text-right py-2 px-2 text-muted-foreground font-medium">Monto</th>
                          <th className="text-left py-2 px-2 text-muted-foreground font-medium">Notas</th>
                          <th className="py-2 px-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {fixedExpenses?.map((exp) => (
                          <tr key={exp?.id} className="border-b border-border/50 hover:bg-muted/30">
                            {editingExpense?.id === exp?.id ? (
                              <>
                                <td className="py-2 px-2">
                                  <select
                                    value={editingExpense?.concept}
                                    onChange={e => setEditingExpense(prev => ({ ...prev, concept: e?.target?.value }))}
                                    className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                                  >
                                    <option value="">Seleccionar...</option>
                                    {EXPENSE_CONCEPTS?.map(c => <option key={c} value={c}>{c}</option>)}
                                  </select>
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="number"
                                    min="0"
                                    value={editingExpense?.amount}
                                    onChange={e => setEditingExpense(prev => ({ ...prev, amount: e?.target?.value }))}
                                    className="w-28 px-2 py-1 text-xs border border-border rounded bg-background text-foreground text-right focus:outline-none focus:ring-1 focus:ring-primary/40"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <input
                                    type="text"
                                    value={editingExpense?.notes || ''}
                                    onChange={e => setEditingExpense(prev => ({ ...prev, notes: e?.target?.value }))}
                                    placeholder="Notas..."
                                    className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
                                  />
                                </td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={handleUpdateExpense}
                                      disabled={savingFinancial}
                                      className="px-2 py-1 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                    >
                                      {savingFinancial ? <Icon name="Loader2" className="w-3 h-3 animate-spin" /> : 'Guardar'}
                                    </button>
                                    <button
                                      onClick={() => setEditingExpense(null)}
                                      className="px-2 py-1 text-xs font-medium rounded border border-border bg-background text-foreground hover:bg-muted transition-colors"
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="py-2 px-2 text-foreground">{exp?.concept}</td>
                                <td className="py-2 px-2 text-right font-medium text-foreground">${fmtMXN(exp?.amount)}</td>
                                <td className="py-2 px-2 text-muted-foreground text-xs">{exp?.notes || '—'}</td>
                                <td className="py-2 px-2">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setEditingExpense({ ...exp })}
                                      className="p-1 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                                      title="Editar gasto"
                                    >
                                      <Icon name="Pencil" className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteExpense(exp?.id)}
                                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                      title="Eliminar gasto"
                                    >
                                      <Icon name="Trash2" className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground mb-3 italic">Sin gastos fijos registrados para este mes.</p>
                )}

                {/* Total */}
                <div className="flex items-center justify-between py-2 px-3 bg-muted/40 rounded-lg border border-border">
                  <span className="text-sm font-semibold text-foreground">Total gastos fijos</span>
                  <span className="text-sm font-bold text-foreground">${fmtMXN(totalFixedExpenses)}</span>
                </div>

                {/* Add expense form */}
                {showAddExpense && (
                  <div className="mt-3 p-3 bg-muted/30 rounded-lg border border-border">
                    <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Nuevo gasto</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                      <select
                        value={newExpense?.concept}
                        onChange={e => setNewExpense(prev => ({ ...prev, concept: e?.target?.value }))}
                        className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        <option value="">Concepto...</option>
                        {EXPENSE_CONCEPTS?.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <input
                        type="number"
                        min="0"
                        value={newExpense?.amount}
                        onChange={e => setNewExpense(prev => ({ ...prev, amount: e?.target?.value }))}
                        placeholder="Monto"
                        className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <input
                        type="text"
                        value={newExpense?.notes}
                        onChange={e => setNewExpense(prev => ({ ...prev, notes: e?.target?.value }))}
                        placeholder="Notas (opcional)"
                        className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={handleSaveExpense}
                        disabled={savingFinancial || !newExpense?.concept || !newExpense?.amount}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {savingFinancial ? <Icon name="Loader2" className="w-3.5 h-3.5 animate-spin" /> : <Icon name="Save" className="w-3.5 h-3.5" />}
                        Guardar
                      </button>
                      <button
                        onClick={() => { setShowAddExpense(false); setNewExpense({ concept: '', amount: '', notes: '' }); setExpenseError(null); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-background text-foreground hover:bg-muted transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                    {expenseError && (
                      <p className="mt-2 text-xs text-red-600 font-medium">{expenseError}</p>
                    )}
                  </div>
                )}
              </div>

              {/* ── B) Margen de contribución promedio ── */}
              <div className="mb-6 p-3 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Margen de contribución promedio (%)</span>
                  {!editingMargin ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-primary">{contributionMargin}%</span>
                      <button
                        onClick={() => { setMarginInput(String(contributionMargin)); setEditingMargin(true); }}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-border bg-background text-foreground hover:bg-muted transition-colors"
                      >
                        <Icon name="Pencil" className="w-3 h-3" />
                        Editar
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0.01"
                        max="100"
                        step="0.01"
                        value={marginInput}
                        onChange={e => setMarginInput(e?.target?.value)}
                        className="w-20 px-2 py-1 text-sm border border-border rounded-md bg-background text-foreground text-right focus:outline-none focus:ring-2 focus:ring-primary/40"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                      <button
                        onClick={handleSaveMargin}
                        disabled={savingFinancial || !marginInput}
                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                      >
                        {savingFinancial ? <Icon name="Loader2" className="w-3 h-3 animate-spin" /> : 'Guardar'}
                      </button>
                      <button
                        onClick={() => setEditingMargin(false)}
                        className="px-2 py-1 text-xs font-medium rounded border border-border bg-background text-foreground hover:bg-muted transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── C) Punto de equilibrio mensual ── */}
              <div className="mb-6 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Punto de equilibrio mensual</span>
                  <span className="text-lg font-bold text-blue-700 dark:text-blue-400">${fmtMXN(breakEvenPoint)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  ${fmtMXN(totalFixedExpenses)} ÷ {contributionMargin}% = ${fmtMXN(breakEvenPoint)}
                </p>
              </div>

              {/* ── D) Indicadores ── */}
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Indicadores del mes</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {/* Total gastos fijos */}
                  <div className="p-3 bg-muted/40 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Total gastos fijos</p>
                    <p className="text-base font-bold text-foreground">${fmtMXN(totalFixedExpenses)}</p>
                  </div>
                  {/* Margen de contribución */}
                  <div className="p-3 bg-muted/40 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Margen de contribución promedio</p>
                    <p className="text-base font-bold text-foreground">{contributionMargin}%</p>
                  </div>
                  {/* Punto de equilibrio */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-xs text-muted-foreground mb-1">Punto de equilibrio mensual</p>
                    <p className="text-base font-bold text-blue-700 dark:text-blue-400">${fmtMXN(breakEvenPoint)}</p>
                  </div>
                  {/* Objetivo del mes */}
                  <div className="p-3 bg-muted/40 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Objetivo del mes</p>
                    <p className="text-base font-bold text-foreground">{goalAmt > 0 ? `$${fmtMXN(goalAmt)}` : <span className="text-muted-foreground italic text-sm">Sin objetivo</span>}</p>
                  </div>
                  {/* Ventas reales del mes */}
                  <div className="p-3 bg-muted/40 rounded-lg border border-border">
                    <p className="text-xs text-muted-foreground mb-1">Ventas reales del mes</p>
                    <p className="text-base font-bold text-foreground">${fmtMXN(realSalesAmt)}</p>
                  </div>
                  {/* Diferencia contra objetivo */}
                  <div className={`p-3 rounded-lg border ${
                    diffVsGoal >= 0
                      ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                  }`}>
                    <p className="text-xs text-muted-foreground mb-1">Diferencia contra objetivo</p>
                    <p className={`text-base font-bold ${
                      diffVsGoal >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                    }`}>
                      {diffVsGoal >= 0 ? '+' : ''}${fmtMXN(diffVsGoal)}
                    </p>
                  </div>
                  {/* Diferencia contra punto de equilibrio */}
                  <div className={`p-3 rounded-lg border ${
                    diffVsBreakEven >= 0
                      ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' :'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'
                  }`}>
                    <p className="text-xs text-muted-foreground mb-1">Diferencia contra punto de equilibrio</p>
                    <p className={`text-base font-bold ${
                      diffVsBreakEven >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'
                    }`}>
                      {diffVsBreakEven >= 0 ? '+' : ''}${fmtMXN(diffVsBreakEven)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                OBJETIVO 3 — Gráfico: Ventas del mes vs Objetivo
            ══════════════════════════════════════════════════════════════ */}
            <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="BarChart2" className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-heading font-semibold text-foreground">Ventas del mes vs Objetivo</h2>
                {loadingChart && <Icon name="Loader2" className="w-4 h-4 text-muted-foreground animate-spin ml-2" />}
              </div>

              {/* ── Goal toast ── */}
              {goalToast && (
                <div className={`mb-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                  goalToast?.type === 'success' ?'bg-green-50 border border-green-200 text-green-800' :'bg-red-50 border border-red-200 text-red-800'
                }`}>
                  <Icon name={goalToast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} className="w-4 h-4 flex-shrink-0" />
                  {goalToast?.msg}
                </div>
              )}

              {/* ── Goal control row ── */}
              <div className="mb-4 flex flex-wrap items-center gap-3 p-3 bg-muted/40 rounded-lg border border-border">
                {!editingGoal ? (
                  <>
                    <span className="text-sm text-muted-foreground font-medium">
                      Objetivo del mes:
                    </span>
                    <span className="text-sm font-semibold text-foreground">
                      {goalAmount > 0 ? `$${fmtMXN(goalAmount)}` : <span className="text-muted-foreground italic">Sin objetivo</span>}
                    </span>
                    <button
                      onClick={() => { setGoalInput(String(goalAmount || '')); setEditingGoal(true); }}
                      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    >
                      <Icon name={goalAmount > 0 ? 'Pencil' : 'Plus'} className="w-3.5 h-3.5" />
                      {goalAmount > 0 ? 'Editar objetivo' : 'Agregar objetivo'}
                    </button>
                  </>
                ) : (
                  <>
                    <label className="text-sm text-muted-foreground font-medium whitespace-nowrap">Objetivo del mes:</label>
                    <input
                      type="number"
                      min="0"
                      value={goalInput}
                      onChange={e => setGoalInput(e?.target?.value)}
                      placeholder="Ej. 150000"
                      className="w-36 px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                    <button
                      onClick={handleSaveGoal}
                      disabled={savingGoal || !goalInput}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {savingGoal ? <Icon name="Loader2" className="w-3.5 h-3.5 animate-spin" /> : <Icon name="Save" className="w-3.5 h-3.5" />}
                      Guardar objetivo
                    </button>
                    <button
                      onClick={() => setEditingGoal(false)}
                      disabled={savingGoal}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-border bg-background text-foreground hover:bg-muted disabled:opacity-50 transition-colors"
                    >
                      Cancelar
                    </button>
                  </>
                )}
              </div>

              {salesChartData?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={salesChartData} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="mes" tick={{ fontSize: 13 }} />
                    <YAxis tickFormatter={(v) => `$${(v / 1000)?.toFixed(0)}k`} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value) => [`$${fmtMXN(value)}`, undefined]} />
                    <Legend />
                    <Bar dataKey="Ventas reales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Objetivo" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                !loadingChart && (
                  <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
                    <Icon name="BarChart2" className="w-8 h-8 mr-2 opacity-30" />
                    Sin datos para el mes actual
                  </div>
                )
              )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                OBJETIVO 1 & 2 — Cotizaciones autorizadas + Generar OT
            ══════════════════════════════════════════════════════════════ */}
            <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="FileCheck" className="w-6 h-6 text-primary" />
                <h2 className="text-xl font-heading font-semibold text-foreground">Cotizaciones autorizadas</h2>
                {loadingApprovedQuotes && <Icon name="Loader2" className="w-4 h-4 text-muted-foreground animate-spin ml-2" />}
                <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  Solo cotizaciones con status = aprobada
                </span>
              </div>

              {/* OT success */}
              {otGenerated && (
                <div className="mb-4 bg-success/10 border border-success/20 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="CheckCircle" className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-success font-medium">
                      OT generada exitosamente y enviada a Taller: {otGenerated?.folio_ot || otGenerated?.id}
                    </p>
                    <button
                      onClick={() => navigate('/work-execution')}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 bg-success text-white text-xs font-medium rounded-md hover:bg-success/90 transition-colors"
                    >
                      <Icon name="Wrench" className="w-3.5 h-3.5" />
                      Ir a Taller
                    </button>
                  </div>
                  <button onClick={() => setOtGenerated(null)} className="text-success hover:text-success/70 flex-shrink-0">
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* OT error */}
              {otError && (
                <div className="mb-4 bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2">
                  <Icon name="AlertCircle" className="w-5 h-5 text-destructive flex-shrink-0" />
                  <p className="text-sm text-destructive font-medium">{otError}</p>
                  <button onClick={() => setOtError(null)} className="ml-auto text-destructive hover:text-destructive/70">
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                </div>
              )}

              {loadingApprovedQuotes ? (
                <div className="flex items-center justify-center gap-2 py-8">
                  <Icon name="Loader2" className="w-5 h-5 text-primary animate-spin" />
                  <p className="text-sm text-muted-foreground">Cargando cotizaciones aprobadas…</p>
                </div>
              ) : approvedQuotes?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="FileCheck" className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No hay cotizaciones aprobadas pendientes de generar OT</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {approvedQuotes?.map((q) => {
                    const v = q?.receptions?.vehicles;
                    const c = q?.receptions?.clients;
                    const vehicleStr = v ? `${v?.marca || ''} ${v?.modelo || ''} ${v?.year || ''}`?.trim() : 'Vehículo';
                    const isSelected = selectedApprovedQuote?.id === q?.id;
                    const authorizedItems = q?.authorized_items_snapshot;
                    const hasItems = authorizedItems && authorizedItems?.length > 0;

                    return (
                      <div
                        key={q?.id}
                        className={`rounded-lg border transition-all cursor-pointer ${
                          isSelected
                            ? 'border-primary bg-primary/5 shadow-sm'
                            : 'border-border bg-background hover:border-primary/40'
                        }`}
                        onClick={() => setSelectedApprovedQuote(isSelected ? null : q)}
                      >
                        {/* Card header */}
                        <div className="flex items-center justify-between p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                              <Icon name="FileCheck" className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-foreground">
                                {q?.folio_cotizacion || `COT-${q?.id?.slice(-6)}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(q?.created_at)?.toLocaleDateString('es-MX')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right hidden sm:block">
                              <p className="text-xs text-muted-foreground">{c?.nombre || 'Cliente'}</p>
                              <p className="text-xs text-muted-foreground">{vehicleStr}</p>
                            </div>
                            {q?.total && (
                              <span className="text-sm font-bold text-primary">
                                ${fmtMXN(q?.total)}
                              </span>
                            )}
                            <Icon
                              name={isSelected ? 'ChevronUp' : 'ChevronDown'}
                              className="w-4 h-4 text-muted-foreground"
                            />
                          </div>
                        </div>
                        {/* Expanded detail */}
                        {isSelected && (
                          <div className="border-t border-border p-4 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {/* Vehicle */}
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Vehículo</p>
                                <div className="space-y-1 text-sm text-foreground">
                                  {v?.marca && <p><span className="text-muted-foreground">Marca/Modelo:</span> {v?.marca} {v?.modelo} {v?.year}</p>}
                                  {v?.vin && <p><span className="text-muted-foreground">VIN:</span> <span className="font-mono">{v?.vin}</span></p>}
                                  {v?.placas && <p><span className="text-muted-foreground">Placas:</span> {v?.placas}</p>}
                                  {v?.motor && <p><span className="text-muted-foreground">Motor:</span> {v?.motor}</p>}
                                </div>
                              </div>
                              {/* Client */}
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Cliente</p>
                                <div className="space-y-1 text-sm text-foreground">
                                  {c?.nombre && <p><span className="text-muted-foreground">Nombre:</span> {c?.nombre}</p>}
                                  {c?.telefono && <p><span className="text-muted-foreground">Teléfono:</span> {c?.telefono}</p>}
                                  {c?.email && <p><span className="text-muted-foreground">Email:</span> {c?.email}</p>}
                                </div>
                              </div>
                            </div>

                            {/* Authorized items */}
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                                Ítems autorizados ({hasItems ? authorizedItems?.length : 0})
                              </p>
                              {!hasItems ? (
                                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
                                  <Icon name="AlertTriangle" className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                  <p className="text-sm text-amber-800">Esta cotización no tiene ítems autorizados guardados.</p>
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {authorizedItems?.map((item, idx) => (
                                    <div key={item?.id || idx} className="flex items-center justify-between text-sm py-1 border-b border-border last:border-0">
                                      <span className="text-foreground">{item?.description}</span>
                                      <span className="text-muted-foreground ml-4 flex-shrink-0">
                                        x{item?.quantity} — ${fmtMXN((item?.price || 0) * (item?.quantity || 1))}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {q?.total && (
                              <div className="flex justify-end pt-2 border-t border-border">
                                <span className="text-base font-heading font-bold text-primary">
                                  Total: ${fmtMXN(q?.total)} MXN
                                </span>
                              </div>
                            )}

                            {/* Generate OT button */}
                            <button
                              onClick={(e) => { e?.stopPropagation(); handleGenerateOT(); }}
                              disabled={generatingOT || !hasItems}
                              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {generatingOT ? (
                                <Icon name="Loader2" className="w-5 h-5 animate-spin" />
                              ) : (
                                <Icon name="ClipboardList" className="w-5 h-5" />
                              )}
                              Generar Orden de Trabajo y Enviar a Taller
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* ══════════════════════════════════════════════════════════════
                OBJETIVO 4 — Cuentas por cobrar
            ══════════════════════════════════════════════════════════════ */}
            <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-heading font-semibold text-foreground">Cuentas por cobrar</h2>
                  {loadingCxc && <Icon name="Loader2" className="w-4 h-4 text-muted-foreground animate-spin" />}
                </div>
                <button
                  onClick={() => setShowCxcForm(!showCxcForm)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  <Icon name="Plus" className="w-4 h-4" />
                  Agregar cuenta por cobrar
                </button>
              </div>

              {/* CxC inline form */}
              {showCxcForm && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-blue-800 mb-3">Nueva cuenta por cobrar</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Monto *</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={cxcForm?.amount}
                        onChange={(e) => setCxcForm(p => ({ ...p, amount: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Fecha de vencimiento</label>
                      <input
                        type="date"
                        value={cxcForm?.due_date}
                        onChange={(e) => setCxcForm(p => ({ ...p, due_date: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Estado</label>
                      <select
                        value={cxcForm?.status}
                        onChange={(e) => setCxcForm(p => ({ ...p, status: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="pagado">Pagado</option>
                        <option value="vencido">Vencido</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Notas</label>
                      <input
                        type="text"
                        placeholder="Opcional"
                        value={cxcForm?.notes}
                        onChange={(e) => setCxcForm(p => ({ ...p, notes: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleSaveCxc}
                      disabled={savingCxc || !cxcForm?.amount}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {savingCxc ? <Icon name="Loader2" className="w-4 h-4 animate-spin" /> : <Icon name="Save" className="w-4 h-4" />}
                      Guardar
                    </button>
                    <button
                      onClick={() => setShowCxcForm(false)}
                      className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* CxC table */}
              {cxcList?.length === 0 && !loadingCxc ? (
                <div className="text-center py-6">
                  <Icon name="TrendingUp" className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No hay cuentas por cobrar registradas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Cliente</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Referencia</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monto</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vencimiento</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Estado</th>
                        <th className="py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cxcList?.map((row) => (
                        <tr key={row?.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 px-3 text-foreground">{row?.clients?.nombre || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground font-mono text-xs">
                            {row?.quote_id ? row?.quote_id?.slice(-8) : '—'}
                          </td>
                          <td className="py-2 px-3 text-right font-semibold text-foreground">${fmtMXN(row?.amount)}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {row?.due_date ? new Date(row?.due_date + 'T00:00:00')?.toLocaleDateString('es-MX') : '—'}
                          </td>
                          <td className="py-2 px-3"><StatusBadge status={row?.status} /></td>
                          <td className="py-2 px-3">
                            {row?.status === 'pendiente' && (
                              <button
                                onClick={() => handleUpdateCxcStatus(row?.id, 'pagado')}
                                className="text-xs text-green-600 hover:underline"
                              >
                                Marcar pagado
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CxC total */}
              <div className="mt-4 pt-3 border-t border-border flex justify-end">
                <span className="text-sm font-semibold text-foreground">
                  Total pendiente por cobrar:{' '}
                  <span className="text-blue-600 text-base">${fmtMXN(totalCxcPendiente)} MXN</span>
                </span>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                OBJETIVO 5 — Cuentas por pagar
            ══════════════════════════════════════════════════════════════ */}
            <div className="mb-6 bg-card rounded-lg border border-border p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingDown" className="w-6 h-6 text-red-600" />
                  <h2 className="text-xl font-heading font-semibold text-foreground">Cuentas por pagar</h2>
                  {loadingCxp && <Icon name="Loader2" className="w-4 h-4 text-muted-foreground animate-spin" />}
                </div>
                <button
                  onClick={() => setShowCxpForm(!showCxpForm)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  <Icon name="Plus" className="w-4 h-4" />
                  Agregar cuenta por pagar
                </button>
              </div>

              {/* CxP inline form */}
              {showCxpForm && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-red-800 mb-3">Nueva cuenta por pagar</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Referencia / Proveedor</label>
                      <input
                        type="text"
                        placeholder="Ej: Factura #123"
                        value={cxpForm?.reference}
                        onChange={(e) => setCxpForm(p => ({ ...p, reference: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Monto *</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={cxpForm?.amount}
                        onChange={(e) => setCxpForm(p => ({ ...p, amount: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Fecha de vencimiento</label>
                      <input
                        type="date"
                        value={cxpForm?.due_date}
                        onChange={(e) => setCxpForm(p => ({ ...p, due_date: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground mb-1">Estado</label>
                      <select
                        value={cxpForm?.status}
                        onChange={(e) => setCxpForm(p => ({ ...p, status: e?.target?.value }))}
                        className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="pagado">Pagado</option>
                        <option value="vencido">Vencido</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={handleSaveCxp}
                      disabled={savingCxp || !cxpForm?.amount}
                      className="flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {savingCxp ? <Icon name="Loader2" className="w-4 h-4 animate-spin" /> : <Icon name="Save" className="w-4 h-4" />}
                      Guardar
                    </button>
                    <button
                      onClick={() => setShowCxpForm(false)}
                      className="px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* CxP table */}
              {cxpList?.length === 0 && !loadingCxp ? (
                <div className="text-center py-6">
                  <Icon name="TrendingDown" className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No hay cuentas por pagar registradas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Proveedor</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Referencia</th>
                        <th className="text-right py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Monto</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vencimiento</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Estado</th>
                        <th className="py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cxpList?.map((row) => (
                        <tr key={row?.id} className="border-b border-border/50 hover:bg-muted/30">
                          <td className="py-2 px-3 text-foreground">{row?.providers?.name || '—'}</td>
                          <td className="py-2 px-3 text-muted-foreground">{row?.reference || '—'}</td>
                          <td className="py-2 px-3 text-right font-semibold text-foreground">${fmtMXN(row?.amount)}</td>
                          <td className="py-2 px-3 text-muted-foreground">
                            {row?.due_date ? new Date(row?.due_date + 'T00:00:00')?.toLocaleDateString('es-MX') : '—'}
                          </td>
                          <td className="py-2 px-3"><StatusBadge status={row?.status} /></td>
                          <td className="py-2 px-3">
                            {row?.status === 'pendiente' && (
                              <button
                                onClick={() => handleUpdateCxpStatus(row?.id, 'pagado')}
                                className="text-xs text-green-600 hover:underline"
                              >
                                Marcar pagado
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* CxP total */}
              <div className="mt-4 pt-3 border-t border-border flex justify-end">
                <span className="text-sm font-semibold text-foreground">
                  Total pendiente por pagar:{' '}
                  <span className="text-red-600 text-base">${fmtMXN(totalCxpPendiente)} MXN</span>
                </span>
              </div>
            </div>

            {/* ══════════════════════════════════════════════════════════════
                Órdenes de Trabajo (existing block — unchanged)
            ══════════════════════════════════════════════════════════════ */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card rounded-lg shadow-elevation-1 p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                      <h2 className="text-xl font-heading font-semibold text-foreground">Órdenes de Trabajo</h2>
                      {ordersIsLive && (
                        <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium">
                          <Icon name="RefreshCw" className="w-4 h-4 animate-spin" />
                          <span>Actualizando</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e?.target?.value)}
                        options={statusOptions}
                        className="min-w-[180px]"
                      />
                      <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e?.target?.value)}
                        options={sortOptions}
                        className="min-w-[180px]"
                      />
                    </div>
                  </div>

                  {ordersLoading ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <Icon name="Loader2" className="w-8 h-8 text-blue-600 animate-spin" />
                      <p className="text-sm text-muted-foreground">Cargando órdenes de trabajo…</p>
                    </div>
                  ) : sortedOrders?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon name="FileText" className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No hay órdenes de trabajo disponibles</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {sortedOrders?.map((order) => (
                        <WorkOrderCard
                          key={order?.id}
                          workOrder={order}
                          isExpanded={expandedOrder === order?.id}
                          onExpand={() => handleExpandOrder(order?.id)}
                          onToggleExpand={() => handleExpandOrder(order?.id)}
                          onStartQuotation={() => handleStartQuotation(order)}
                          onSendToInspection={handleSendToInspection}
                          onSelectClient={() => handleSelectClient(order)}
                          onContactClient={() => handleContactClient(order)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6 md:space-y-8">
                {selectedWorkOrder?.cliente && (
                  <>
                    <ClientHistoryPanel
                      clientId={selectedWorkOrder?.cliente?.rfc}
                      history={mockClientHistory}
                    />
                    <CommunicationPanel
                      clientData={selectedWorkOrder?.cliente}
                      workOrderData={selectedWorkOrder}
                      onSendEmail={handleSendEmail}
                      onMakeCall={handleMakeCall}
                      onSendWhatsApp={handleSendWhatsApp}
                    />
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default SalesManagement;